import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { FiArrowUpRight, FiChevronRight, FiSearch, FiX } from "react-icons/fi";
import { AuthContext } from "../../../context/AuthContext";
import { getSettingsGroups } from "./settingsMenu";

const ACCENTS = {
  brand: { icon: "bg-brand-50 text-brand-600", bar: "bg-brand-500", dot: "bg-brand-500" },
  teal: { icon: "bg-teal-50 text-teal-600", bar: "bg-teal-500", dot: "bg-teal-500" },
  purple: { icon: "bg-purple-50 text-purple-600", bar: "bg-purple-500", dot: "bg-purple-500" },
  amber: { icon: "bg-amber-50 text-amber-600", bar: "bg-amber-500", dot: "bg-amber-500" },
};

const NAVBAR_H = 64;
const EDGE = 12;
const GAP = 10;

const matches = (text, query) => text.toLowerCase().includes(query);

const filterItems = (items, query) =>
  items.flatMap((item) => {
    if (!query || matches(item.label, query) || matches(item.description, query)) return [item];
    if (!item.children) return [];
    const children = item.children.filter((child) => matches(child.label, query));
    return children.length ? [{ ...item, children }] : [];
  });

const ItemIcon = ({ icon: Icon, accent }) => (
  <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${accent.icon}`}>
    <Icon size={16} />
  </div>
);

const ROW =
  "group relative flex w-full items-center gap-3.5 px-4 py-3 text-left transition-colors focus:outline-none focus-visible:bg-gray-50";

const RowText = ({ item }) => (
  <div className="min-w-0 flex-1 sm:flex sm:items-baseline sm:gap-3">
    <p className="shrink-0 text-sm font-medium text-gray-900">{item.label}</p>
    <p className="truncate text-[13px] text-gray-500">{item.description}</p>
  </div>
);

const SettingRow = ({ item, accent }) => (
  <Link to={item.to} className={`${ROW} hover:bg-gray-50`}>
    <ItemIcon icon={item.icon} accent={accent} />
    <RowText item={item} />
    <FiArrowUpRight
      size={15}
      className="shrink-0 text-gray-300 transition-transform duration-150 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gray-600"
    />
  </Link>
);

const GroupRow = ({ item, accent, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    aria-expanded={selected}
    className={`${ROW} ${selected ? "bg-gray-50" : "hover:bg-gray-50"}`}
  >
    {selected && <span className={`absolute inset-y-0 left-0 w-0.5 ${accent.bar}`} />}
    <ItemIcon icon={item.icon} accent={accent} />
    <RowText item={item} />
    <FiChevronRight
      size={15}
      className={`shrink-0 transition-colors ${selected ? "text-gray-700" : "text-gray-300 group-hover:text-gray-600"}`}
    />
  </button>
);

const GroupPopover = ({ item, accent, anchor, onClose }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const panel = ref.current;
    if (!panel || !anchor.isConnected) return;
    const r = anchor.getBoundingClientRect();
    if (r.bottom < NAVBAR_H || r.top > window.innerHeight) {
      onClose();
      return;
    }
    const w = panel.offsetWidth;
    const h = panel.offsetHeight;
    const side = r.right + GAP + w <= window.innerWidth - EDGE;
    const left = side ? r.right + GAP : Math.max(EDGE, r.right - w);
    const top = Math.max(
      NAVBAR_H + EDGE,
      Math.min(side ? r.top : r.bottom + GAP / 2, window.innerHeight - h - EDGE)
    );
    const caret = side ? Math.max(14, Math.min(r.top + r.height / 2 - top, h - 14)) : null;
    setPos({ top, left, caret });
  }, [anchor, onClose]);

  useLayoutEffect(place, [place, item.children.length]);

  useEffect(() => {
    const onDown = (e) => {
      if (!ref.current?.contains(e.target) && !anchor.contains(e.target)) onClose();
    };
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [place, anchor, onClose]);

  return createPortal(
    <div
      ref={ref}
      style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? "visible" : "hidden" }}
      className="fixed z-40 w-64 rounded-xl border border-gray-200 bg-white shadow-pop"
    >
      {pos?.caret != null && (
        <span
          style={{ top: pos.caret }}
          className="absolute -left-[5px] h-2.5 w-2.5 -translate-y-1/2 rotate-45 border-b border-l border-gray-200 bg-white"
        />
      )}
      <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5">
        <span className={`h-1.5 w-1.5 rounded-full ${accent.dot}`} />
        <p className="flex-1 text-[13px] font-semibold text-gray-900">{item.label}</p>
        <button
          type="button"
          onClick={onClose}
          title="Close"
          className="-mr-1.5 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
        >
          <FiX size={14} />
        </button>
      </div>
      <div className="max-h-[calc(100vh-10rem)] overflow-y-auto p-1.5">
        {item.children.map((child) => (
          <Link
            key={child.to}
            to={child.to}
            className="group flex items-center justify-between rounded-lg px-3 py-2 text-[13px] font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:bg-gray-50"
          >
            <span className="truncate">{child.label}</span>
            <FiChevronRight
              size={14}
              className="shrink-0 text-gray-300 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-gray-600"
            />
          </Link>
        ))}
      </div>
    </div>,
    document.body
  );
};

const SettingsPage = () => {
  const { user } = useContext(AuthContext);
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [openGroup, setOpenGroup] = useState(null);
  const searchRef = useRef(null);

  const groups = useMemo(() => getSettingsGroups(user), [user]);
  const closeGroup = useCallback(() => setOpenGroup(null), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const q = query.trim().toLowerCase();
  const sections = groups
    .filter((g) => activeTab === "All" || g.title === activeTab)
    .map((g) => ({ ...g, items: filterItems(g.items, q) }))
    .filter((g) => g.items.length);

  const selected =
    openGroup &&
    sections
      .flatMap((s) => s.items.map((item) => ({ item, accent: ACCENTS[s.accent] })))
      .find(({ item }) => item.children && item.label === openGroup.label);

  const tabs = [
    { title: "All", count: groups.reduce((sum, g) => sum + g.items.length, 0) },
    ...groups.map((g) => ({ title: g.title, count: g.items.length, accent: ACCENTS[g.accent] })),
  ];

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your account, system configuration, portals and templates.
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <FiSearch size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && setQuery("")}
            placeholder="Search settings..."
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-8 pr-9 text-[13px] outline-none transition-colors placeholder:text-gray-400 hover:border-gray-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              title="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <FiX size={13} />
            </button>
          ) : (
            <kbd className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 rounded border border-gray-200 bg-gray-50 px-1.5 text-[11px] font-medium text-gray-400">
              /
            </kbd>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {tabs.map((tab) => {
          const active = activeTab === tab.title;
          return (
            <button
              key={tab.title}
              type="button"
              onClick={() => setActiveTab(tab.title)}
              className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors
                ${active
                  ? "border-gray-900 bg-gray-900 text-white"
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-900"}`}
            >
              {tab.accent && <span className={`h-1.5 w-1.5 rounded-full ${tab.accent.dot}`} />}
              {tab.title}
              <span className={`text-[11px] ${active ? "text-gray-300" : "text-gray-400"}`}>{tab.count}</span>
            </button>
          );
        })}
      </div>

      {sections.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center">
          <p className="text-sm font-medium text-gray-700">No settings match "{query.trim()}"</p>
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setActiveTab("All");
            }}
            className="mt-2 text-[13px] font-medium text-brand-600 hover:text-brand-700"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {sections.map((section) => {
            const accent = ACCENTS[section.accent];
            return (
              <section key={section.title}>
                <div className="mb-2 flex items-center gap-2 px-1">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${accent.dot}`} />
                  <h2 className="text-[13px] font-semibold uppercase tracking-wide text-gray-500">
                    {section.title}
                  </h2>
                </div>
                <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-card">
                  {section.items.map((item) =>
                    item.children ? (
                      <GroupRow
                        key={item.label}
                        item={item}
                        accent={accent}
                        selected={openGroup?.label === item.label}
                        onSelect={(e) => {
                          const anchor = e.currentTarget;
                          setOpenGroup((current) =>
                            current?.label === item.label ? null : { label: item.label, anchor }
                          );
                        }}
                      />
                    ) : (
                      <SettingRow key={item.label} item={item} accent={accent} />
                    )
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {selected && (
        <GroupPopover
          key={openGroup.label}
          item={selected.item}
          accent={selected.accent}
          anchor={openGroup.anchor}
          onClose={closeGroup}
        />
      )}
    </div>
  );
};

export default SettingsPage;